// src/components/admin/GestionFeriados.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import feriadoService from '../../services/feriadoService';
import diasInhabilesJudicialesService from '../../services/diasInhabilesJudicialesService';
import ModalConfirmacionCambioEstado from '../ModalConfirmacionCambioEstado';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionFeriados.css';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../mobile/BottomSheet';
import BotonFiltros from '../mobile/BotonFiltros';
import ChipsFiltros from '../mobile/ChipsFiltros';

// ─── Paleta de colores por tipo ───────────────────────────────────────────────
const TIPO_CONFIG = {
    NACIONAL:     { bg: '#DBEAFE', border: '#3B82F6', text: '#1D4ED8', icon: '🇦🇷', label: 'Nacional',      rowClass: 'gf-row-nacional',    badgeClass: 'gf-badge-nacional' },
    TRASLADABLE:  { bg: '#EDE9FE', border: '#8B5CF6', text: '#6D28D9', icon: '🔄', label: 'Trasladable',   rowClass: 'gf-row-trasladable', badgeClass: 'gf-badge-trasladable' },
    PUENTE:       { bg: '#FEF3C7', border: '#F59E0B', text: '#B45309', icon: '🌉', label: 'Puente',         rowClass: 'gf-row-puente',      badgeClass: 'gf-badge-puente' },
    NO_LABORABLE: { bg: '#FFF7ED', border: '#FB923C', text: '#9A3412', icon: '⚠️', label: 'No Laborable',  rowClass: 'gf-row-no_laborable',badgeClass: 'gf-badge-no_laborable' },
    PROVINCIAL:   { bg: '#F1F5F9', border: '#64748B', text: '#374151', icon: '🏛️', label: 'Provincial',    rowClass: 'gf-row-provincial',  badgeClass: 'gf-badge-provincial' },
    LOCAL:        { bg: '#E0F2FE', border: '#0EA5E9', text: '#0369A1', icon: '📍', label: 'Local',          rowClass: 'gf-row-local',       badgeClass: 'gf-badge-local' },
    JUDICIAL:     { bg: '#DCFCE7', border: '#10B981', text: '#047857', icon: '⚖️', label: 'Judicial',      rowClass: 'gf-row-judicial',    badgeClass: 'gf-badge-judicial' },
};
const getTipoConfig = (tipo) => TIPO_CONFIG[tipo?.toUpperCase()] ?? TIPO_CONFIG.NACIONAL;


// ─── (Mini calendario eliminado — los feriados se reflejan en el calendario lateral) ───

// ─── Componente principal ─────────────────────────────────────────────────────
const GestionFeriados = () => {
    // Estado — feriados normales
    const [feriados, setFeriados]           = useState([]);
    const [loading, setLoading]             = useState(false);
    const [error, setError]                 = useState(null);
    const [success, setSuccess]             = useState(null);

    // Estado — modales CRUD normales
    const [showModal, setShowModal]                 = useState(false);
    const [showBulkModal, setShowBulkModal]         = useState(false);
    const [showImportApiModal, setShowImportApiModal] = useState(false);
    const [editingFeriado, setEditingFeriado]       = useState(null);
    const [showModalEliminar, setShowModalEliminar] = useState(false);
    const [feriadoAEliminar, setFeriadoAEliminar]   = useState(null);
    const [eliminando, setEliminando]               = useState(false);

    // Estado — formularios
    const [formData, setFormData]   = useState({ fecha: '', descripcion: '', tipo: 'NACIONAL' });
    const [bulkText, setBulkText]   = useState('');
    const [anioImportar, setAnioImportar] = useState(new Date().getFullYear());

    // Estado — filtros normales
    const [searchNombre, setSearchNombre] = useState('');
    const [searchFecha, setSearchFecha]   = useState('');
    const [filterTipo, setFilterTipo]     = useState('TODOS');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    // Estado — vista activa
    const [vistaFeriados, setVistaFeriados] = useState('normales');

    // Estado — judiciales
    const [judiciales, setJudiciales]               = useState([]);
    const [judicialesLoading, setJudicialesLoading] = useState(false);
    const [judicialesError, setJudicialesError]     = useState(null);
    const [judicialesAnio, setJudicialesAnio]       = useState(new Date().getFullYear());
    const [importandoAnio, setImportandoAnio]       = useState(false);
    const [estadoSync, setEstadoSync]               = useState(null);
    const [showEliminarJudicialModal, setShowEliminarJudicialModal] = useState(false);
    const [judicialAEliminar, setJudicialAEliminar] = useState(null);
    const [eliminandoJudicial, setEliminandoJudicial] = useState(false);

    // Estado — modal importar judicial
    const [showImportarJudicialModal, setShowImportarJudicialModal] = useState(false);
    const [importModalLocalidad, setImportModalLocalidad]           = useState('CÓRDOBA');
    const [importModalAnio, setImportModalAnio]                     = useState(new Date().getFullYear());
    const [countdownSegundos, setCountdownSegundos]                 = useState(0);
    const countdownRef = useRef(null);

    // ─── Carga inicial + cleanup de countdown ───────────────────────────────
    useEffect(() => {
        cargarFeriados();
        diasInhabilesJudicialesService.obtenerEstadoSync()
            .then(r => setEstadoSync(r?.data || null))
            .catch(() => {});
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, []);

    // ─── Carga de judiciales al cambiar tab/año ──────────────────────────────
    const cargarJudiciales = useCallback(async (anio) => {
        try {
            setJudicialesLoading(true);
            setJudicialesError(null);
            const [resJudiciales, resSync] = await Promise.allSettled([
                diasInhabilesJudicialesService.obtenerPorAnio(anio),
                diasInhabilesJudicialesService.obtenerEstadoSync(),
            ]);
            setJudiciales(resJudiciales.status === 'fulfilled' && Array.isArray(resJudiciales.value?.data)
                ? resJudiciales.value.data : []);
            if (resSync.status === 'fulfilled') setEstadoSync(resSync.value?.data || null);
        } catch (err) {
            console.error('Error al cargar días inhábiles judiciales:', err);
            setJudicialesError('No se pudieron cargar los días inhábiles judiciales.');
        } finally {
            setJudicialesLoading(false);
        }
    }, []);

    useEffect(() => {
        if (vistaFeriados === 'judiciales') cargarJudiciales(judicialesAnio);
    }, [vistaFeriados, judicialesAnio, cargarJudiciales]);

    // ─── Feriados filtrados ──────────────────────────────────────────────────
    const feriadosFiltrados = useMemo(() => {
        let lista = [...feriados];
        if (filterTipo !== 'TODOS') lista = lista.filter(f => f.tipo === filterTipo);
        if (searchNombre.trim()) {
            const t = searchNombre.toLowerCase().trim();
            lista = lista.filter(f => (f.descripcion?.toLowerCase() || '').includes(t));
        }
        if (searchFecha.trim()) {
            lista = lista.filter(f => (f.fecha || '').includes(searchFecha.trim()));
        }
        return lista;
    }, [feriados, filterTipo, searchNombre, searchFecha]);

    // ─── Handlers de feriados normales ───────────────────────────────────────
    const cargarFeriados = async () => {
        try {
            setLoading(true);
            const response = await feriadoService.obtenerTodos();
            const anioActual = new Date().getFullYear();
            const filtrados = (response.data || [])
                .filter(f => new Date(f.fecha).getFullYear() >= anioActual)
                .sort((a, b) => a.fecha.localeCompare(b.fecha));
            setFeriados(filtrados);
        } catch (err) {
            console.error('Error al cargar feriados:', err);
            setError('Error al cargar los feriados');
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = (feriado = null) => {
        if (feriado) {
            setEditingFeriado(feriado);
            setFormData({ fecha: feriado.fecha, descripcion: feriado.descripcion, tipo: feriado.tipo || 'NACIONAL' });
        } else {
            setEditingFeriado(null);
            setFormData({ fecha: '', descripcion: '', tipo: 'NACIONAL' });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingFeriado(null);
        setFormData({ fecha: '', descripcion: '', tipo: 'NACIONAL' });
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            if (editingFeriado) {
                await feriadoService.actualizar(editingFeriado.idFeriado, formData);
                setSuccess('Feriado actualizado correctamente.');
            } else {
                await feriadoService.crear(formData);
                setSuccess('Feriado creado correctamente.');
            }
            handleCloseModal();
            await cargarFeriados();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.response?.data || 'Error al guardar el feriado.');
        }
    };

    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            const lines = bulkText.trim().split('\n');
            const feriadosData = [];
            for (const line of lines) {
                if (!line.trim()) continue;
                const parts = line.split('|').map(p => p.trim());
                if (parts.length < 2) throw new Error(`Formato inválido: "${line}". Use: YYYY-MM-DD | Descripción | Tipo`);
                feriadosData.push({ fecha: parts[0], descripcion: parts[1], tipo: parts[2] || 'NACIONAL' });
            }
            if (!feriadosData.length) throw new Error('No se encontraron feriados válidos.');
            const response = await feriadoService.crearMultiples(feriadosData);
            setSuccess(`Se agregaron ${response.data.length} feriados correctamente.`);
            setShowBulkModal(false);
            setBulkText('');
            await cargarFeriados();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || err.response?.data || 'Error al guardar los feriados.');
        }
    };

    const handleEliminar = (id) => {
        setFeriadoAEliminar(feriados.find(f => f.idFeriado === id) || null);
        setShowModalEliminar(true);
    };

    const confirmarEliminar = async () => {
        if (!feriadoAEliminar) return;
        setEliminando(true);
        try {
            await feriadoService.eliminarPermanente(feriadoAEliminar.idFeriado);
            setSuccess('Feriado eliminado correctamente.');
            setShowModalEliminar(false);
            setFeriadoAEliminar(null);
            await cargarFeriados();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Error al eliminar el feriado.');
            setShowModalEliminar(false);
            setFeriadoAEliminar(null);
        } finally {
            setEliminando(false);
        }
    };

    const handleImportarDesdeAPI = async () => {
        if (!anioImportar || anioImportar < 2000 || anioImportar > 2100) {
            setError('Ingrese un año válido entre 2000 y 2100.');
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const response = await feriadoService.importarDesdeAPI(anioImportar);
            const n = response.data.length;
            setSuccess(n > 0
                ? `Se importaron ${n} feriados del año ${anioImportar}.`
                : `No hay feriados nuevos para ${anioImportar} (ya están todos cargados).`);
            setShowImportApiModal(false);
            await cargarFeriados();
            setTimeout(() => setSuccess(null), 5000);
        } catch (err) {
            setError(err.response?.data || err.message || 'Error al importar feriados.');
        } finally {
            setLoading(false);
        }
    };

    // ─── Handlers judiciales ─────────────────────────────────────────────────
    const iniciarCountdown = (segundos = 60) => {
        setCountdownSegundos(segundos);
        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
            setCountdownSegundos(prev => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current);
                    countdownRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const detenerCountdown = () => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
        setCountdownSegundos(0);
    };

    const handleImportarAnioJudicial = async () => {
        if (!importModalAnio || importModalAnio < 2000 || importModalAnio > 2100) {
            setError('Ingrese un año válido entre 2000 y 2100.');
            return;
        }
        try {
            setImportandoAnio(true);
            setError(null);

            // Guardar el timestamp previo para detectar cuándo termina el sync
            let syncAnterior = null;
            try {
                const estadoPrevio = await diasInhabilesJudicialesService.obtenerEstadoSync();
                syncAnterior = estadoPrevio?.data?.ultimoSyncEn ?? null;
            } catch (_) {}

            // Disparar importación — el backend responde 202 de inmediato
            await diasInhabilesJudicialesService.importarAnio(
                importModalAnio,
                importModalLocalidad || 'CÓRDOBA'
            );

            // Countdown visual mientras se procesa en el backend
            iniciarCountdown(180);

            // Polling a /estado-sync hasta que ultimoSyncEn cambie (máx 3 minutos)
            let estadoFinal = null;
            for (let intento = 0; intento < 60; intento++) {
                await new Promise(r => setTimeout(r, 3000));
                try {
                    const resp = await diasInhabilesJudicialesService.obtenerEstadoSync();
                    const syncActual = resp?.data?.ultimoSyncEn ?? null;
                    if (syncActual && syncActual !== syncAnterior) {
                        estadoFinal = resp.data;
                        break;
                    }
                } catch (_) {}
            }

            detenerCountdown();
            const nuevos = estadoFinal?.ultimoSyncNuevos ?? 0;
            setSuccess(nuevos > 0
                ? `Se importaron ${nuevos} días inhábiles judiciales para ${importModalAnio}.`
                : `No se encontraron días nuevos para ${importModalAnio} (ya están cargados o no hay datos publicados).`);
            setShowImportarJudicialModal(false);
            setJudicialesAnio(importModalAnio);
            await cargarJudiciales(importModalAnio);
            setTimeout(() => setSuccess(null), 6000);
        } catch (err) {
            detenerCountdown();
            setError(err.response?.data || err.message || 'Error al importar días inhábiles judiciales.');
        } finally {
            setImportandoAnio(false);
        }
    };

    const handleCerrarImportModal = () => {
        if (importandoAnio) return;
        setShowImportarJudicialModal(false);
        setError(null);
    };

    const handleEliminarJudicial = (dia) => {
        setJudicialAEliminar(dia);
        setShowEliminarJudicialModal(true);
    };

    const confirmarEliminarJudicial = async () => {
        if (!judicialAEliminar) return;
        setEliminandoJudicial(true);
        try {
            await diasInhabilesJudicialesService.eliminarPermanente(judicialAEliminar.id);
            setSuccess('Día inhábil judicial eliminado.');
            setShowEliminarJudicialModal(false);
            setJudicialAEliminar(null);
            await cargarJudiciales(judicialesAnio);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Error al eliminar el día inhábil judicial.');
            setShowEliminarJudicialModal(false);
        } finally {
            setEliminandoJudicial(false);
        }
    };

    // ─── Helpers de formato ──────────────────────────────────────────────────
    const formatFechaCorta = (fecha) =>
        new Date(fecha + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const formatFechaLarga = (fecha) =>
        new Date(fecha + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="gestion-feriados">

            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="gf-header">
                <div className="gf-header-actions">
                    <Button variant="outline-secondary" size="sm" onClick={cargarFeriados} disabled={loading} title="Recargar">
                        <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`} />
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => setShowBulkModal(true)}>
                        <i className="bi bi-file-earmark-text me-1" />Carga masiva
                    </Button>
                    <Button variant="outline-success" size="sm" onClick={() => setShowImportApiModal(true)}>
                        <i className="bi bi-cloud-download me-1" />Importar API
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => handleShowModal()}>
                        <i className="bi bi-plus-circle me-1" />Nuevo feriado
                    </Button>
                </div>
            </div>


            {/* ── Alertas ──────────────────────────────────────────────────── */}
            <div className="gf-alerts">
                {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
            </div>

            {/* ── Tabs ─────────────────────────────────────────────────────── */}
            <div className="gf-tabs">
                <button
                    className={`gf-tab ${vistaFeriados === 'normales' ? 'active' : ''}`}
                    onClick={() => setVistaFeriados('normales')}
                >
                    <i className="bi bi-calendar-event" />
                    Nacionales y Comunes
                    <span className="gf-tab-count">{feriados.length}</span>
                </button>
                <button
                    className={`gf-tab tab-judicial ${vistaFeriados === 'judiciales' ? 'active' : ''}`}
                    onClick={() => setVistaFeriados('judiciales')}
                >
                    <i className="bi bi-building-fill-check" />
                    Judiciales
                    <span className="gf-tab-count">{estadoSync?.totalEnBD ?? '…'}</span>
                </button>
            </div>

            {/* ── Tab: Normales ─────────────────────────────────────────────── */}
            {vistaFeriados === 'normales' && (
                <div className="gf-main-panel">

                        {/* Filtros (el "Recargar" está en las acciones de arriba) */}
                        {(() => {
                            const limpiarFiltros = () => { setSearchNombre(''); setSearchFecha(''); setFilterTipo('TODOS'); };
                            const chipsFiltros = [
                                searchNombre.trim() !== '' && { key: 'nombre', label: searchNombre, onQuitar: () => setSearchNombre('') },
                                searchFecha && { key: 'fecha', label: searchFecha, onQuitar: () => setSearchFecha('') },
                                filterTipo !== 'TODOS' && { key: 'tipo', label: TIPO_CONFIG[filterTipo]?.label || filterTipo, onQuitar: () => setFilterTipo('TODOS') },
                            ].filter(Boolean);
                            const resumen = `Mostrando ${feriadosFiltrados.length} de ${feriados.length} feriados`;

                            const campoDescripcion = (
                                <CampoFiltro etiqueta="Descripción" icono="bi-search" htmlFor="filtro-feriado-descripcion" ancho>
                                    <Form.Control
                                        id="filtro-feriado-descripcion"
                                        className="panel-filtros-input"
                                        type="text"
                                        placeholder="Ej: Navidad, Revolución…"
                                        value={searchNombre}
                                        onChange={e => setSearchNombre(e.target.value)}
                                    />
                                </CampoFiltro>
                            );
                            const campoFecha = (
                                <CampoFiltro etiqueta="Fecha" icono="bi-calendar-event" htmlFor="filtro-feriado-fecha">
                                    <Form.Control
                                        id="filtro-feriado-fecha"
                                        className="panel-filtros-input"
                                        type="date"
                                        value={searchFecha}
                                        onChange={e => setSearchFecha(e.target.value)}
                                    />
                                </CampoFiltro>
                            );
                            const campoTipo = (
                                <CampoFiltro etiqueta="Tipo" icono="bi-tag" htmlFor="filtro-feriado-tipo">
                                    <Form.Select
                                        id="filtro-feriado-tipo"
                                        className="panel-filtros-input"
                                        value={filterTipo}
                                        onChange={e => setFilterTipo(e.target.value)}
                                    >
                                        <option value="TODOS">Todos los tipos</option>
                                        <option value="NACIONAL">Nacional</option>
                                        <option value="TRASLADABLE">Trasladable</option>
                                        <option value="PUENTE">Puente</option>
                                        <option value="NO_LABORABLE">No Laborable</option>
                                        <option value="PROVINCIAL">Provincial</option>
                                        <option value="LOCAL">Local</option>
                                    </Form.Select>
                                </CampoFiltro>
                            );

                            return esMobile ? (
                                <div className="mb-3">
                                    <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                                    <ChipsFiltros chips={chipsFiltros} />
                                    {sheetFiltrosAbierto && (
                                        <BottomSheet
                                            titulo="Filtros"
                                            onCerrar={() => setSheetFiltrosAbierto(false)}
                                            accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar', onClick: limpiarFiltros } : undefined}
                                        >
                                            {campoDescripcion}
                                            {campoFecha}
                                            {campoTipo}
                                        </BottomSheet>
                                    )}
                                    <small className="d-block mt-2 text-muted">{resumen}</small>
                                </div>
                            ) : (
                                <PanelFiltros hayFiltros={chipsFiltros.length > 0} onLimpiar={limpiarFiltros} resumen={resumen}>
                                    {campoDescripcion}
                                    {campoFecha}
                                    {campoTipo}
                                </PanelFiltros>
                            );
                        })()}

                        {/* Tabla */}
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-3 text-muted small">Cargando feriados...</p>
                            </div>
                        ) : (
                            <div className="gf-table-wrapper">
                                <Table className="gf-table" hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Fecha</th>
                                            <th>Descripción</th>
                                            <th>Tipo</th>
                                            <th>Estado</th>
                                            <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {feriadosFiltrados.length === 0 ? (
                                            <tr>
                                                <td colSpan="5">
                                                    <div className="gf-empty-state">
                                                        <i className="bi bi-calendar-x" />
                                                        {feriados.length === 0
                                                            ? 'No hay feriados registrados'
                                                            : 'No coinciden con los filtros aplicados'}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : feriadosFiltrados.map(f => {
                                            const cfg = getTipoConfig(f.tipo);
                                            return (
                                                <tr key={f.idFeriado} className={cfg.rowClass}>
                                                    <td>
                                                        <div className="gf-fecha-cell">
                                                            <strong>{formatFechaCorta(f.fecha)}</strong><br />
                                                            <small>{formatFechaLarga(f.fecha)}</small>
                                                        </div>
                                                    </td>
                                                    <td style={{ fontWeight: 500 }}>{f.descripcion}</td>
                                                    <td>
                                                        <span className={`gf-badge ${cfg.badgeClass}`}>
                                                            {cfg.icon} {cfg.label}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {f.activo !== false
                                                            ? <span className="gf-badge-activo">Activo</span>
                                                            : <span className="gf-badge-inactivo">Inactivo</span>}
                                                    </td>
                                                    <td className="text-end">
                                                        <MenuAccionesFila acciones={[
                                                            { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleShowModal(f) },
                                                            { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleEliminar(f.idFeriado) },
                                                        ]} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                </div>
            )}

            {/* ── Tab: Judiciales ───────────────────────────────────────────── */}
            {vistaFeriados === 'judiciales' && (
                <div className="gf-judicial-panel">

                    {/* Header judicial */}
                    <div className="gf-judicial-header">
                        <div>
                            <h6>Días Inhábiles Judiciales — Poder Judicial de Córdoba</h6>
                            <small>Almacenados en BD. El sistema los verifica automáticamente cada mañana a las 06:00.</small>
                            {estadoSync && (
                                <div className="mt-2">
                                    <span className="gf-judicial-sync-badge">
                                        <i className="bi bi-clock-history" />
                                        Último sync: {estadoSync.ultimoSyncEn
                                            ? new Date(estadoSync.ultimoSyncEn).toLocaleString('es-AR')
                                            : 'Sin registro'} — {estadoSync.ultimoSyncResultado} ({estadoSync.totalEnBD ?? 0} en BD)
                                    </span>
                                </div>
                            )}
                        </div>
                        <Button variant="outline-secondary" size="sm"
                            onClick={() => cargarJudiciales(judicialesAnio)} disabled={judicialesLoading}
                            className="d-flex align-items-center gap-1">
                            <i className={`bi bi-arrow-clockwise ${judicialesLoading ? 'spin' : ''}`} /> Recargar
                        </Button>
                    </div>

                    {/* Barra de importación anual */}
                    <div className="gf-judicial-import-bar">
                        <Button
                            variant="success" size="sm"
                            onClick={() => setShowImportarJudicialModal(true)}
                            disabled={importandoAnio || judicialesLoading}
                            className="d-flex align-items-center gap-2"
                        >
                            <i className="bi bi-cloud-download" /> Importar año
                        </Button>
                        <small className="text-muted" style={{ maxWidth: 320 }}>
                            Consulta Justicia Córdoba y guarda los días no cubiertos por feriados normales.
                        </small>
                    </div>

                    {/* Alerta de error */}
                    {judicialesError && (
                        <Alert variant="warning" className="mx-3 mt-3" dismissible onClose={() => setJudicialesError(null)}>
                            {judicialesError}
                        </Alert>
                    )}

                    {/* Tabla judicial */}
                    {judicialesLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="success" />
                            <p className="mt-3 text-muted small">Cargando días inhábiles judiciales…</p>
                        </div>
                    ) : (
                        <div>
                            <div className="px-3 pt-3 pb-1">
                                <small className="text-muted">
                                    <i className="bi bi-info-circle me-1" />
                                    {judiciales.length} días inhábiles judiciales para {judicialesAnio}
                                </small>
                            </div>
                            <Table className="gf-table" hover responsive>
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Descripción</th>
                                        <th>Jurisdicción</th>
                                        <th>Cargado</th>
                                        <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {judiciales.length === 0 ? (
                                        <tr>
                                            <td colSpan="5">
                                                <div className="gf-empty-state">
                                                    <i className="bi bi-building" />
                                                    No hay días inhábiles judiciales para {judicialesAnio}.<br />
                                                    <small>Usá el botón <strong>"Importar año"</strong> para traerlos desde Justicia Córdoba.</small>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : judiciales.map(dia => (
                                        <tr key={dia.id} className="gf-row-judicial">
                                            <td>
                                                <div className="gf-fecha-cell">
                                                    <strong>
                                                        {new Date(dia.fecha + 'T00:00:00').toLocaleDateString('es-AR', {
                                                            weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric'
                                                        })}
                                                    </strong>
                                                </div>
                                            </td>
                                            <td style={{ fontWeight: 500 }}>{dia.descripcion}</td>
                                            <td>
                                                <span className="gf-badge gf-badge-judicial">⚖️ {dia.jurisdiccion}</span>
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    {dia.creadoEn ? new Date(dia.creadoEn).toLocaleDateString('es-AR') : '—'}
                                                </small>
                                            </td>
                                            <td className="text-end">
                                                <MenuAccionesFila acciones={[
                                                    { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleEliminarJudicial(dia) },
                                                ]} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Modal confirmación eliminar judicial */}
                    <ModalConfirmacionCambioEstado
                        isOpen={showEliminarJudicialModal}
                        onClose={() => { setShowEliminarJudicialModal(false); setJudicialAEliminar(null); }}
                        onConfirm={confirmarEliminarJudicial}
                        titulo="Eliminar Día Inhábil Judicial"
                        mensaje={judicialAEliminar
                            ? `¿Eliminar "${judicialAEliminar.descripcion}" (${new Date(judicialAEliminar.fecha + 'T00:00:00').toLocaleDateString('es-AR')})?`
                            : ''}
                        cargando={eliminandoJudicial}
                        textoBotonConfirmar="Eliminar"
                        textoBotonCancelar="Cancelar"
                        variantBotonConfirmar="danger"
                    />
                </div>
            )}

            {/* ── Modal: Importar año judicial ─────────────────────────────── */}
            <Modal
                show={showImportarJudicialModal}
                onHide={handleCerrarImportModal}
                centered
                backdrop={importandoAnio ? 'static' : true}
                keyboard={!importandoAnio}
                className="gf-modal"
            >
                <Modal.Header closeButton={!importandoAnio}>
                    <Modal.Title>
                        <i className="bi bi-cloud-download me-2" />
                        Importar Días Inhábiles Judiciales
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!importandoAnio ? (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Localidad / Jurisdicción</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={importModalLocalidad}
                                    onChange={e => setImportModalLocalidad(e.target.value.toUpperCase())}
                                    placeholder="Ej: CÓRDOBA, RÍO CUARTO…"
                                />
                                <Form.Text className="text-muted">
                                    Nombre de la localidad tal como aparece en el sitio de Justicia Córdoba.
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Año a importar</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="2000"
                                    max="2100"
                                    value={importModalAnio}
                                    onChange={e => setImportModalAnio(parseInt(e.target.value) || new Date().getFullYear())}
                                    style={{ width: 120 }}
                                />
                            </Form.Group>
                            <Alert variant="info" className="mb-0 d-flex align-items-start gap-2" style={{ fontSize: '0.85rem' }}>
                                <i className="bi bi-info-circle-fill mt-1 flex-shrink-0" />
                                <span>
                                    El sistema consulta el sitio de Justicia Córdoba mes por mes.
                                    El proceso puede demorar entre <strong>60 y 90 segundos</strong>.
                                </span>
                            </Alert>
                        </Form>
                    ) : (
                        <div className="gf-import-progress">
                            <div className="gf-countdown-wrapper">
                                <div
                                    className="gf-countdown-ring"
                                    style={{ '--pct': `${Math.round(((60 - countdownSegundos) / 60) * 100)}%` }}
                                >
                                    <span className="gf-countdown-num">{countdownSegundos}</span>
                                </div>
                                <div className="gf-countdown-label">
                                    {countdownSegundos > 0 ? 'segundos estimados' : 'Finalizando…'}
                                </div>
                            </div>
                            <p className="text-muted text-center mb-0" style={{ fontSize: '0.88rem' }}>
                                Consultando <strong>{importModalLocalidad}</strong> — año <strong>{importModalAnio}</strong>
                                <br />
                                <small>No cierre esta ventana mientras se importa.</small>
                            </p>
                        </div>
                    )}
                    {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCerrarImportModal} disabled={importandoAnio}>
                        Cancelar
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleImportarAnioJudicial}
                        disabled={importandoAnio}
                        className="d-flex align-items-center gap-2"
                    >
                        {importandoAnio
                            ? <><Spinner animation="border" size="sm" /> Importando…</>
                            : <><i className="bi bi-cloud-download" /> Importar</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Modal: Crear/Editar feriado ───────────────────────────────── */}
            <Modal show={showModal} onHide={handleCloseModal} centered className="gf-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{editingFeriado ? 'Editar Feriado' : 'Nuevo Feriado'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Fecha *</Form.Label>
                            <Form.Control type="date" value={formData.fecha} required
                                onChange={e => setFormData({ ...formData, fecha: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Descripción *</Form.Label>
                            <Form.Control type="text" placeholder="Ej: Día de la Independencia" required
                                value={formData.descripcion}
                                onChange={e => setFormData({ ...formData, descripcion: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label>Tipo</Form.Label>
                            <Form.Select value={formData.tipo} onChange={e => setFormData({ ...formData, tipo: e.target.value })}>
                                <option value="NACIONAL">🇦🇷 Nacional (Inamovible)</option>
                                <option value="TRASLADABLE">🔄 Trasladable</option>
                                <option value="PUENTE">🌉 Puente Turístico</option>
                                <option value="NO_LABORABLE">⚠️ No Laborable</option>
                                <option value="PROVINCIAL">🏛️ Provincial</option>
                                <option value="LOCAL">📍 Local</option>
                            </Form.Select>
                        </Form.Group>
                        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={handleCloseModal}>Cancelar</Button>
                            <Button variant="primary" type="submit">{editingFeriado ? 'Actualizar' : 'Guardar'}</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* ── Modal: Carga masiva ───────────────────────────────────────── */}
            <Modal show={showBulkModal} onHide={() => { setShowBulkModal(false); setBulkText(''); setError(null); }} size="lg" centered className="gf-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Carga Masiva de Feriados</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info" className="mb-3">
                        <i className="bi bi-info-circle me-2" />
                        <strong>Formato:</strong> un feriado por línea:<br />
                        <code>YYYY-MM-DD | Descripción | Tipo</code>
                        <small className="d-block text-muted mt-1">El tipo es opcional (por defecto: NACIONAL)</small>
                    </Alert>
                    <Alert variant="secondary">
                        <strong>Ejemplo:</strong>
                        <pre className="mb-0 small">
{`2025-01-01 | Año Nuevo | NACIONAL
2025-05-01 | Día del Trabajador
2025-07-09 | Día de la Independencia | NACIONAL`}
                        </pre>
                    </Alert>
                    <Form onSubmit={handleBulkSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Lista de feriados</Form.Label>
                            <Form.Control as="textarea" rows={8} required
                                value={bulkText} onChange={e => setBulkText(e.target.value)}
                                placeholder="2025-01-01 | Año Nuevo | NACIONAL" />
                        </Form.Group>
                        {error && <Alert variant="danger">{error}</Alert>}
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => { setShowBulkModal(false); setBulkText(''); }}>Cancelar</Button>
                            <Button variant="primary" type="submit">
                                <i className="bi bi-upload me-1" />Cargar feriados
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* ── Modal: Importar desde API ─────────────────────────────────── */}
            <Modal show={showImportApiModal} onHide={() => setShowImportApiModal(false)} centered className="gf-modal">
                <Modal.Header closeButton>
                    <Modal.Title><i className="bi bi-cloud-download me-2" />Importar desde API Oficial</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info">
                        <i className="bi bi-info-circle me-2" />
                        Importa los feriados nacionales oficiales de Argentina desde la API pública de <strong>argentinadatos.com</strong>.
                    </Alert>
                    <Form.Group className="mb-4">
                        <Form.Label>Año a importar *</Form.Label>
                        <Form.Control type="number" min="2000" max="2100" value={anioImportar}
                            onChange={e => setAnioImportar(parseInt(e.target.value))} />
                        <Form.Text className="text-muted">Año para el cual importar los feriados (2000–2100)</Form.Text>
                    </Form.Group>
                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={() => setShowImportApiModal(false)} disabled={loading}>Cancelar</Button>
                        <Button variant="success" onClick={handleImportarDesdeAPI} disabled={loading}
                            className="d-flex align-items-center gap-2">
                            {loading
                                ? <><Spinner animation="border" size="sm" /> Importando…</>
                                : <><i className="bi bi-cloud-download" /> Importar feriados</>}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* ── Modal: Confirmar eliminar feriado normal ──────────────────── */}
            <ModalConfirmacionCambioEstado
                isOpen={showModalEliminar}
                onClose={() => { setShowModalEliminar(false); setFeriadoAEliminar(null); }}
                onConfirm={confirmarEliminar}
                titulo="Eliminar Feriado"
                mensaje={feriadoAEliminar
                    ? `¿Eliminar "${feriadoAEliminar.descripcion}" (${formatFechaCorta(feriadoAEliminar.fecha)})? Esta acción no se puede deshacer.`
                    : ''}
                cargando={eliminando}
                textoBotonConfirmar="Eliminar"
                textoBotonCancelar="Cancelar"
                variantBotonConfirmar="danger"
            />
        </div>
    );
};

export default GestionFeriados;
